import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthProvider';
import { getMe } from '../Components/UserAdmin/userAdminApi';

/**
 * What the signed-in person may open, from User Administration (GET /api/admin/me).
 * The sidebar uses it to show the Administration section to IT Managers only.
 * If the server says the account was deactivated, this signs them out.
 */

const UserAccessContext = createContext({ loading: true, access: null, isItManager: false, refresh: () => {} });

const RECHECK_MS = 60 * 1000;

export function UserAccessProvider({ children }) {
    const { logOut } = useAuth();
    const logOutRef = useRef(logOut);
    logOutRef.current = logOut;
    const [state, setState] = useState({ loading: true, access: null });
    const lastCheck = useRef(0);

    const refresh = useCallback(async () => {
        lastCheck.current = Date.now();
        try {
            const access = await getMe();
            setState({ loading: false, access });
        } catch (err) {
            if (err.blocked) {
                alert(err.message);
                logOutRef.current();
                return;
            }
            // The server no longer knows this sign-in (its sessions live in memory, so a restart ends
            // them). Sign in again instead of silently hiding what this person may open.
            if (err.status === 401) {
                logOutRef.current();
                return;
            }
            setState({ loading: false, access: null });
        }
    }, []);

    useEffect(() => {
        refresh();
        // Coming back to the tab re-checks, so a deactivation takes effect without a reload.
        const onFocus = () => { if (Date.now() - lastCheck.current > RECHECK_MS) refresh(); };
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [refresh]);

    const value = useMemo(() => ({
        loading: state.loading,
        access: state.access,
        isItManager: !!(state.access && state.access.isItManager),
        refresh,
    }), [state, refresh]);

    return <UserAccessContext.Provider value={value}>{children}</UserAccessContext.Provider>;
}

export const useUserAccess = () => useContext(UserAccessContext);
